import {
	infiniteQueryOptions,
	keepPreviousData,
	queryOptions,
	useSuspenseInfiniteQuery,
	useSuspenseQuery,
} from '@tanstack/react-query'

import { useCurrentTenant } from '@/shared/auth/store'
import { time } from '@/shared/datetime/utils/time'

import {
	fetchActivities,
	fetchActivityDetails,
	fetchPlaybookActivities,
	getActionStatus,
} from './api'

import type { WorkflowActionStatus } from './types'

/**
 * Query keys for Activity
 * - list: Fetch all actions
 * - details: Fetch a single action
 * - status: Fetch the status of an action, should use polling
 */
export const activityKeys = {
	all: ['activity'] as const,
	lists: () => [...activityKeys.all, 'lists'] as const,
	list: (
		tenant: string,
		status?: string,
		type?: 'adhoc' | 'workflow',
		limit: number = 20,
	) =>
		[...activityKeys.lists(), tenant, status, type, limit, 'no-cache'] as const,
	listPlaybooks: (tenant: string, status?: WorkflowActionStatus) =>
		[...activityKeys.lists(), tenant, 'playbooks', status, 'no-cache'] as const,
	details: (tenant: string) =>
		[...activityKeys.all, 'details', tenant] as const,
	detail: (tenant: string, action_uuid: string | null) =>
		[...activityKeys.details(tenant), action_uuid, 'no-cache'] as const,
	status: (tenant: string, action_uuid: string | null) =>
		[
			...activityKeys.detail(tenant, action_uuid),
			'status',
			'no-cache',
		] as const,
}

/**
 * Query Options for Activity
 * - status: Fetch the status of an action, should use polling
 */
export const activityQueries = {
	list: (
		tenant: string,
		status?: string,
		type?: 'adhoc' | 'workflow',
		limit: number = 20,
	) =>
		infiniteQueryOptions({
			queryKey: activityKeys.list(tenant, status, type, limit),
			queryFn: ({ pageParam }) =>
				fetchActivities(tenant, pageParam, limit, status, type),
			// Short-lived cache: 30s window dedupes in-flight requests while polling
			staleTime: time(30, 's'),
			gcTime: time(30, 's'),
			refetchInterval: time(30, 's'),
			initialPageParam: 1,
			getNextPageParam: (lastPage, _all, lastPageParam) => {
				if (lastPage.length < limit) {
					return undefined
				}
				return lastPageParam + 1
			},
		}),
	listPlaybooks: (tenant: string, status?: WorkflowActionStatus) =>
		infiniteQueryOptions({
			queryKey: activityKeys.listPlaybooks(tenant, status),
			queryFn: ({ pageParam }) =>
				fetchPlaybookActivities(tenant, pageParam, status),
			staleTime: time(30, 's'),
			gcTime: time(30, 's'),
			refetchInterval: time(30, 's'),
			placeholderData: keepPreviousData,
			initialPageParam: 0,
			getNextPageParam: (lastPage, _all, lastPageParam) => {
				const LIMIT = 15

				if (lastPage.length < LIMIT) {
					return undefined
				}
				return lastPageParam + 1
			},
		}),

	detail: (tenant: string, action_uuid: string | null) =>
		queryOptions({
			queryKey: activityKeys.detail(tenant, action_uuid),
			queryFn: ({ signal }) =>
				fetchActivityDetails(tenant, action_uuid, signal),
			// Short-lived cache: 30s window dedupes in-flight requests while polling
			staleTime: time(30, 's'),
			gcTime: time(30, 's'),
		}),

	status: (tenant: string, action_uuid: string | null) =>
		queryOptions({
			queryKey: activityKeys.status(tenant, action_uuid),
			queryFn: ({ signal }) => getActionStatus(tenant, action_uuid, signal),
			// Short-lived cache: 30s window dedupes in-flight requests while polling
			staleTime: time(30, 's'),
			gcTime: time(30, 's'),

			// Refetch every 4 seconds while the action is still running
			refetchInterval: ({ state: { data } }) => {
				// Still running while status is neither success nor error (terminal states)
				const isStillRunning =
					data?.summary.status !== 'success' && data?.summary.status !== 'error'

				// Once the action reaches a terminal state, stop polling
				return isStillRunning ? time(4, 's') : 0
			},
		}),
}

/**
 * Custom hook to fetch activity logs using ReactQuery
 * @param {string | undefined} tenant - The tenant ID
 * @param {function} select - Optional function to select a subset of data
 */
export const useActivities = (status?: string, type?: 'adhoc' | 'workflow') => {
	const tenant = useCurrentTenant()

	return useSuspenseInfiniteQuery(activityQueries.list(tenant, status, type))
}

/**
 * Custom hook to fetch activity logs using ReactQuery
 * @param {string | undefined} tenant - The tenant ID
 * @param {function} select - Optional function to select a subset of data
 */
export const useActivitiesPlaybooks = (status?: WorkflowActionStatus) => {
	const tenant = useCurrentTenant()

	return useSuspenseInfiniteQuery(activityQueries.listPlaybooks(tenant, status))
}

/**
 * Custom hook to fetch action activity details using ReactQuery
 * @param {string | null} identifier - The identifier of the activity
 */
export const useActivityDetails = (identifier: string | null) => {
	const tenant = useCurrentTenant()

	return useSuspenseQuery(activityQueries.detail(tenant, identifier))
}
